import { dateInPastForZone } from '@insights-ltd/design-library/utils';
import { QueryStatus } from '@tanstack/react-query';
import {
  EventSummaryResponse,
  GroupResponse,
  OrganisationResponse,
  PractitionerSummaryData,
  useGetGroupByOrganisation,
  UserInvite,
} from 'api';
import {
  useGetOpenPractitionerInvites,
  useGetOrganisation,
  useGetOrgEvents,
  useGetPractitionersSummary,
} from 'api/organisations/organisationHooks';
import useIsPermitted from 'components/hooks/useIsPermitted';
import { User } from 'contexts/AuthContext';
import { sortCancelledExperiences } from 'utils/mappers/sorting';
import { queryStatus } from 'utils/queryStatus';
import { OrganisationViewType } from './typeGuard';

const useGetOrganisationData = (
  organisationId: string,
  tabValue: OrganisationViewType,
  user: User,
): {
  upcomingEvents?: EventSummaryResponse[];
  pastEvents?: EventSummaryResponse[];
  cancelledEvents?: EventSummaryResponse[];
  invites?: UserInvite[];
  organisation?: OrganisationResponse;
  canViewAllOrgs?: boolean;
  practitioners?: PractitionerSummaryData[];
  organisationGroup?: GroupResponse | null;
  status: QueryStatus;
} => {
  const { isPermitted: canViewAllOrgs, status: isPermittedStatus } =
    useIsPermitted([
      {
        action: 'Organisation_ReadAll',
        scope: 'Global',
      },
    ]);

  const { data: organisation, status: getOrganisationStatus } =
    useGetOrganisation(organisationId);

  const isGroupQueryEnabled =
    !!user?.permissions.Organisation_Group_ReadAll &&
    user.permissions.Organisation_Group_ReadAll !== 'None';

  const { data: organisationGroup, status: getGroupByOrganisationStatus } =
    useGetGroupByOrganisation(organisationId, {
      enabled: isGroupQueryEnabled,
    });

  const shouldFetchActiveEvents =
    tabValue === 'upcomingEvents' || tabValue === 'pastEvents';
  const shouldFetchCancelledEvents = tabValue === 'cancelled';

  const { data: activeEvents, status: getActiveEventsStatus } = useGetOrgEvents(
    organisationId,
    'ACTIVE',
    { enabled: shouldFetchActiveEvents },
  );

  const { data: cancelledEventsData, status: getCancelledEventsStatus } =
    useGetOrgEvents(organisationId, 'CANCELLED', {
      enabled: shouldFetchCancelledEvents,
    });

  const upcomingEvents = shouldFetchActiveEvents
    ? activeEvents?.filter(
        (ev) => !dateInPastForZone(ev.extendedEndDate, ev.timezone),
      )
    : undefined;

  const pastEvents = shouldFetchActiveEvents
    ? activeEvents?.filter((ev) =>
        dateInPastForZone(ev.extendedEndDate, ev.timezone),
      )
    : undefined;

  const cancelledEvents = shouldFetchCancelledEvents
    ? sortCancelledExperiences(cancelledEventsData)
    : undefined;

  const shouldFetchInvites = tabValue === 'invites';
  const { data: invites, status: getInvitesStatus } =
    useGetOpenPractitionerInvites(organisationId, {
      enabled: shouldFetchInvites,
    });

  const shouldFetchPractitioners = tabValue === 'practitioners';
  const { data: practitioners, status: getPractitionersStatus } =
    useGetPractitionersSummary(organisationId, {
      enabled: shouldFetchPractitioners,
    });

  const relevantStatuses = [
    getOrganisationStatus,
    isPermittedStatus,
    ...(isGroupQueryEnabled ? [getGroupByOrganisationStatus] : []),
    ...(shouldFetchActiveEvents ? [getActiveEventsStatus] : []),
    ...(shouldFetchCancelledEvents ? [getCancelledEventsStatus] : []),
    ...(shouldFetchInvites ? [getInvitesStatus] : []),
    ...(shouldFetchPractitioners ? [getPractitionersStatus] : []),
  ];

  const status = queryStatus(...relevantStatuses);

  return status === 'success'
    ? {
        upcomingEvents,
        pastEvents,
        cancelledEvents,
        invites,
        organisation,
        canViewAllOrgs,
        practitioners,
        organisationGroup,
        status,
      }
    : {
        status,
      };
};

export default useGetOrganisationData;
